<template>
	<span
		:class="[colorClass, customClasses]"
		class="icon"
		role="img"
		:aria-label="`${token} icon`"
		@click="$emit('click', $event)"
	>
		<span
			ref="icon-background"
			:class="{
				'icon__background': rounded
			}"
		>
			<i
				v-if="type === 'fontAwesome'"
				:class="`${fontAwesomeClasses} fa-${token}`"
			/>

			<span
				v-if="type === 'centiva'"
				:class="centivaIconClasses"
				class="icon__custom-font"
			/>
		</span>
	</span>
</template>

<script>
export default {
	name: 'Icon',

	props: {
		color: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the colour of the Icon. The colour provided must be one of those in the $colors-list.',
		},

		customClasses: {
			type: String,
			required: false,
			default: null,
			description: 'Sets additional custom CSS classes',
		},

		rounded: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Icon will have a round background',
		},

		spin: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Icon will spin',
		},

		token: {
			type: String,
			required: true,
			description: 'Sets which FontAwesome or custom Centiva Icon will appear',
		},

		weight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the Icon',
		},
	},

	computed: {
		type() {
			const centivaIcons = [
				'display-full',
				'display-half',
				'display-video',
				'separator-dots',
				'separator-line',
				'modules-grid',
				'helio-envelope',
				'helio-envelope-slash',
				'helio-floor',
				'helio-level',
				'helio-bungalow',
				'helio-cottage',
				'helio-2-plex',
				'helio-360-degrees',
				'helio-4-plex',
				'helio-5-plex',
				'helio-8-plex',
				'helio-16-plex',
				'helio-commercial-residentiel',
				'helio-water-view',
				'helio-woodstove',
				'helio-sous-sol',
				'helio-sous-sol-six',
				'helio-save',
				'helio-save-slash',
				'language-schedule',
			];

			return (centivaIcons.includes(this.token))
				? 'centiva'
				: 'fontAwesome';
		},

		colorClass() {
			return this.color ? `icon--color-${this.color}` : '';
		},

		fontAwesomeClasses() {
			let classes = '';

			switch (this.weight) {
				case 'light':
					classes += 'fal';
					break;

				case 'bold':
					classes += 'fas';
					break;

				case 'brand':
					classes += 'fab';
					break;

				default:
				case 'regular':
					classes += 'far';
					break;
			}

			if (this.spin) {
				classes += ' fa-spin';
			}

			return classes;
		},

		centivaIconClasses() {
			let classes = '';

			switch (this.weight) {
				case 'light':
					classes += 'icon__custom-font--light';
					break;

				case 'bold':
					classes += 'icon__custom-font--bold';
					break;

				default:
				case 'regular':
					classes += 'icon__custom-font--regular';
					break;
			}

			classes += ` icon-${this.token}`;

			return classes;
		},
	},

	mounted() {
		this.$nextTick(() => {
			this.backgroundSize();
		});
	},
	updated() {
		this.$nextTick(() => {
			this.backgroundSize();
		});
	},
	methods: {
		backgroundSize() {
			if (!this.$refs['icon-background']) {
				return;
			}

			if (!this.rounded) {
				this.$refs['icon-background'].style.backgroundColor = 'transparent';
				return;
			}

			const size = Math.max(this.$el.clientHeight, this.$el.clientWidth);
			this.$refs['icon-background'].style.width = `${size}px`;
			this.$refs['icon-background'].style.height = `${size}px`;
		},
	},
};
</script>

<style lang="scss" src="./icon.scss" />
